import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDives } from '../api';

function fmtDuration(sec) {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
}

const MAANDEN_KORT = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(String(dateStr).substring(0, 10));
  return `${d.getDate()} ${MAANDEN_KORT[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDateRange(startStr, endStr) {
  if (!startStr) return '—';
  const s = new Date(String(startStr).substring(0, 10));
  const e = new Date(String(endStr).substring(0, 10));
  const sDay  = s.getDate();
  const sMon  = MAANDEN_KORT[s.getMonth()];
  const sYear = s.getFullYear();
  const eDay  = e.getDate();
  const eMon  = MAANDEN_KORT[e.getMonth()];
  const eYear = e.getFullYear();
  if (startStr === endStr) return `${sDay} ${sMon} ${sYear}`;
  if (sYear === eYear && s.getMonth() === e.getMonth()) return `${sDay}–${eDay} ${sMon} ${sYear}`;
  if (sYear === eYear) return `${sDay} ${sMon} – ${eDay} ${eMon} ${sYear}`;
  return `${sDay} ${sMon} ${sYear} – ${eDay} ${eMon} ${eYear}`;
}

export default function TripsPage() {
  const [dives, setDives]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getDives()
      .then(data => setDives(Array.isArray(data) ? data : (data.dives || [])))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Reizen laden…</div>;
  if (error)   return <div className="error">Kon reizen niet laden: {error}</div>;

  // Filter duiken met trip-veld
  const tripDives = dives.filter(d => d.trip && d.trip.trim() !== '');

  // Groepeer per individuele trip-waarde (gesplitst op komma)
  const tripMap = {};
  for (const dive of tripDives) {
    const tripNames = dive.trip.split(',').map(t => t.trim()).filter(Boolean);
    for (const tripName of tripNames) {
      if (!tripMap[tripName]) tripMap[tripName] = [];
      tripMap[tripName].push(dive);
    }
  }

  // Bouw trip-objecten
  const trips = Object.entries(tripMap).map(([name, ds]) => {
    const sorted = [...ds].sort((a, b) => String(a.dive_date).localeCompare(String(b.dive_date)));
    const start_date  = sorted[0]?.dive_date;
    const end_date    = sorted[sorted.length - 1]?.dive_date;
    const dive_count  = ds.length;
    const total_sec   = ds.reduce((sum, d) => sum + (d.duration_sec || 0), 0);
    const max_depth   = Math.max(...ds.map(d => parseFloat(d.max_depth_m) || 0));
    const locSet      = new Set(ds.map(d => d.location_name).filter(Boolean));
    const locations   = [...locSet];
    return { name, dive_count, start_date, end_date, total_sec, max_depth, dives: sorted, locations };
  });

  // Sorteer op meest recente duikdatum aflopend
  trips.sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)));

  const toggleExpand = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 4 }}>
            JE REIZEN
          </div>
          <h1 className="page-title" style={{ margin: 0 }}>Reizen</h1>
        </div>
        <div style={{ fontSize: 28 }}>✈️</div>
      </div>

      {trips.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✈️</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Nog geen reizen</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Voeg een reislabel toe aan je duiken om ze hier te zien.
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            {trips.length} {trips.length === 1 ? 'reis' : 'reizen'}
          </div>
          {trips.map(trip => {
            const isOpen = !!expanded[trip.name];
            return (
              <div key={trip.name} className="card" style={{ marginBottom: 12 }}>
                {/* Trip header */}
                <div
                  onClick={() => toggleExpand(trip.name)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{trip.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                        {fmtDateRange(trip.start_date, trip.end_date)}
                      </div>
                      {/* Stats row */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        <div>
                          <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{trip.dive_count}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>duiken</span>
                        </div>
                        <div>
                          <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{fmtDuration(trip.total_sec)}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>totaal</span>
                        </div>
                        {trip.max_depth > 0 && (
                          <div>
                            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{trip.max_depth}m</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>diepste</span>
                          </div>
                        )}
                      </div>
                      {trip.locations.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                          📍 {trip.locations.join(', ')}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 18, color: 'var(--text-muted)', marginLeft: 12, marginTop: 2 }}>
                      {isOpen ? '▲' : '▼'}
                    </div>
                  </div>
                </div>

                {/* Expanded dive list */}
                {isOpen && (
                  <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 8 }}>
                      Duiken
                    </div>
                    {trip.dives.map((d, i) => (
                      <div
                        key={d.id}
                        onClick={() => navigate(`/dives/${d.id}`)}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '9px 0',
                          borderBottom: i < trip.dives.length - 1 ? '1px solid var(--border)' : 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>#{d.dive_number}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 10 }}>
                            {fmtDate(d.dive_date)}
                          </span>
                          {d.location_name && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 10 }}>
                              · {d.location_name}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          {d.max_depth_m && (
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--blue)' }}>{d.max_depth_m}m</span>
                          )}
                          {d.duration_sec && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDuration(d.duration_sec)}</span>
                          )}
                          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>›</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
